import xhr from 'xhr';

/**
 * GET /api/zoom/recordings/summary?date=YYYY-MM-DD
 * KPIs del pipeline para una fecha (default: hoy).
 */
export async function getRecordingsSummary({ dateFrom = '', dateTo = '', all = false } = {}) {
  const params = new URLSearchParams();
  if (all) params.append('all', '1');
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  const url = `/zoom/recordings/summary${params.toString() ? '?' + params.toString() : ''}`;
  const { data } = await xhr.get(url);
  return data;
}

/**
 * GET /api/zoom/recordings/logs
 * Lista paginada + filtros. Ver backend para query params soportados.
 */
export async function getRecordingLogs({
  page = 1,
  pageSize = 20,
  status = '',
  dateFrom = '',
  dateTo = '',
  search = '',
} = {}) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (status) params.append('status', status);
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  if (search) params.append('search', search);
  const { data } = await xhr.get(`/zoom/recordings/logs?${params.toString()}`);
  return data;
}

/**
 * GET /api/zoom/recordings/logs/:id
 */
export async function getRecordingLogDetail(id) {
  const { data } = await xhr.get(`/zoom/recordings/logs/${id}`);
  return data;
}

/**
 * POST /api/zoom/recordings/logs/:id/retry
 * Reintenta una grabación fallida / sin clase / sin archivo. Responde 409 con
 * `message` legible cuando no se puede (ya no está en Zoom, sin live-class…).
 */
export async function retryRecording(id) {
  const { data } = await xhr.post(`/zoom/recordings/logs/${id}/retry`);
  return data;
}

export const RETRYABLE_STATUSES = ['failed', 'unmatched', 'skipped_no_live_class', 'skipped_no_file'];

/**
 * GET /api/zoom/accounts
 * Lista de cuentas activas en Zoom (via /v2/users). Cada cuenta trae
 * `has_recording_today` para marcar visualmente cuáles ya emitieron
 * grabación hoy en el popover del chip.
 */
export async function getZoomAccounts() {
  const { data } = await xhr.get('/zoom/accounts');
  return data;
}

/**
 * Etiquetas legibles y colores por status del pipeline.
 * Alineado con el enum del zoom-recording-log content-type en klazia-api.
 */
export const STATUS_LABELS = {
  webhook_received: 'Webhook recibido',
  enqueued: 'En cola',
  download_started: 'Descargando',
  upload_completed: 'Subiendo',
  recorded_class_created: 'Subida OK',
  failed: 'Error',
  skipped_short: 'Descartada (corta)',
  skipped_no_file: 'Sin archivo',
  unmatched: 'Sin match',
  skipped_no_live_class: 'Sin clase asociada',
  duplicate: 'Duplicada',
};

// palette: verde OK · azul en curso · rojo error · ámbar sin-match · gris neutro
export const STATUS_COLOR = {
  recorded_class_created: 'green',
  enqueued: 'blue',
  webhook_received: 'blue',
  download_started: 'blue',
  upload_completed: 'blue',
  failed: 'red',
  unmatched: 'orange',
  skipped_short: 'gray',
  skipped_no_file: 'gray',
  skipped_no_live_class: 'gray',
  duplicate: 'gray',
};

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'recorded_class_created', label: 'Subida OK' },
  { value: 'enqueued', label: 'En cola' },
  { value: 'failed', label: 'Error' },
  { value: 'unmatched', label: 'Sin match' },
  { value: 'skipped_short', label: 'Descartada (corta)' },
  { value: 'skipped_no_file', label: 'Sin archivo' },
  { value: 'skipped_no_live_class', label: 'Sin clase asociada' },
  { value: 'duplicate', label: 'Duplicada' },
];
